import { useState, type FormEvent } from "react";
import { privacyPolicyData, serviceOptions } from "@/mocks/homeContent";
import PrivacyPolicyModal from "./PrivacyPolicyModal";

const portalId = "246786539";
const formId = "128b3e96-cf51-4f12-8db6-b1398567fead";

const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

export default function CTASection() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      setSubmitting(false);
      return;
    }

    const message = formData.get("message") as string;
    if (message && message.length > 500) {
      setError("Message must be 500 characters or fewer.");
      setSubmitting(false);
      return;
    }

    try {
      const body = {
        fields: [
          {
            name: "Full Name",
            value: formData.get("full_name"),
          },
          {
            name: "email",
            value: formData.get("email"),
          },
          {
            name: "phone",
            value: formData.get("phone"),
          },
          {
            name: "description",
            value: formData.get("description"),
          },
          {
            name: "services_interest",
            value: formData.get("services_interest"),
          },
        ],
      };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="cta"
      className="relative py-24 md:py-32 px-4 md:px-6 lg:px-12 bg-background-100"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle, oklch(var(--foreground-950)) 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,oklch(var(--primary-100)/0.08),transparent_65%)] translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,oklch(var(--accent-100)/0.06),transparent_65%)] -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto">
        {/* Section label */}
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/80 border border-background-200/60 text-xs font-medium text-foreground-600 tracking-wide uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            Let&apos;s get started
          </span>
        </div>

        {submitted ? (
          <div className="max-w-lg mx-auto text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-accent-100 flex items-center justify-center">
              <i className="ri-check-line text-accent-600 text-3xl w-10 h-10 flex items-center justify-center" />
            </div>
            <h4 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
              Thank you for reaching out
            </h4>
            <p className="text-base text-foreground-600 max-w-md mx-auto leading-relaxed">
              Our team will review your request and get back to you within one
              business day. We look forward to learning about your project.
            </p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
            {/* Left: compelling copy */}
            <div className="flex-1 lg:pt-6">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.15] tracking-tight">
                Ready to{" "}
                <span className="italic text-foreground-600 font-normal">
                  transform
                </span>{" "}
                how your business{" "}
                <span className="italic text-foreground-600 font-normal">
                  operates
                </span>{" "}
                with technology?
              </h2>

              <p className="mt-5 text-base text-foreground-600 leading-relaxed max-w-md">
                Tell us about your goals, challenges, or the project you have in
                mind. Our team will craft a tailored plan that fits your budget
                and timeline — no pressure, no fluff.
              </p>

              {/* Trust points */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/50">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-shield-check-line text-accent-600 text-sm w-5 h-5 flex items-center justify-center" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      Free consultation
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      No commitment required
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/50">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-accent-600 text-sm w-5 h-5 flex items-center justify-center" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      Same-day response
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      We reply within hours
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/50">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-team-line text-accent-600 text-sm w-5 h-5 flex items-center justify-center" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      Dedicated advisor
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      Personal point of contact
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/50">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-draft-line text-accent-600 text-sm w-5 h-5 flex items-center justify-center" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      Tailored proposal
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      Built around your needs
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: the form */}
            <div className="flex-1 w-full lg:max-w-[480px]">
              <div className="bg-background-50 rounded-2xl border border-background-200/60 p-6 md:p-8">
                <form
                  data-readdy-form
                  id="consultation-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="full_name"
                        className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide"
                      >
                        Full Name
                      </label>
                      <input
                        id="full_name"
                        name="full_name"
                        required
                        placeholder="John Smith"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide"
                      >
                        Work Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide"
                      >
                        Phone
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        required
                        placeholder="Your phone number"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="service"
                        className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide"
                      >
                        Service Interest
                      </label>
                      <select
                        id="service"
                        name="services_interest"
                        required
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23898989' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 12px center",
                          paddingRight: "36px",
                        }}
                      >
                        <option value="">Select a service</option>
                        {serviceOptions.map((svc) => (
                          <option key={svc} value={svc}>
                            {svc}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide"
                    >
                      Tell us about your needs
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="Describe what you are looking for, your current challenges, or any questions you have..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                    />
                    <div className="text-right mt-1">
                      <span className="text-[10px] text-foreground-400">
                        {charCount}/500
                      </span>
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                      <i className="ri-error-warning-line text-red-500 text-sm w-4 h-4 flex items-center justify-center" />
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {error}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center px-8 py-3.5 rounded-full bg-foreground-950 text-white text-sm font-semibold hover:bg-foreground-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      "Schedule Free Consultation"
                    )}
                  </button>

                  <p className="text-center text-[11px] text-foreground-400 leading-relaxed">
                    By submitting, you agree to our{" "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacy(true);
                      }}
                      className="underline underline-offset-2 hover:text-foreground-600 transition-colors"
                    >
                      Privacy Policy
                    </a>{" "}
                    and consent to being contacted about our services.
                  </p>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      {showPrivacy && (
        <PrivacyPolicyModal
          data={privacyPolicyData}
          onClose={() => setShowPrivacy(false)}
        />
      )}
    </section>
  );
}
